import { encodeBase64Utf8 } from '@liza/ui/utils/base64';
import type { DocumentReference, DocumentReferenceContent } from '@medplum/fhirtypes';

/** C-CDA documents are stored with this attachment `contentType`. */
const CDA_CONTENT_TYPE = 'application/xml';

/** Content types `DocumentAttachment` already renders inline; a CDA doc with one of these needs no transform. */
const INLINE_RENDERABLE_PREFIXES = ['text/html', 'application/pdf', 'text/markdown', 'text/plain', 'image/'];

const hasPayload = (content: DocumentReferenceContent): boolean =>
  Boolean(content.attachment?.url || content.attachment?.data);

const hasInlineRenderableContent = (doc: DocumentReference): boolean =>
  (doc.content ?? []).some(
    (content) =>
      hasPayload(content) &&
      INLINE_RENDERABLE_PREFIXES.some((prefix) => content.attachment?.contentType?.startsWith(prefix)),
  );

const findCcdaContent = (doc: DocumentReference): DocumentReferenceContent | undefined =>
  (doc.content ?? []).find(
    (content) => content.attachment?.url && content.attachment.contentType?.startsWith(CDA_CONTENT_TYPE),
  );

/**
 * A DocumentReference needs server-side CDA rendering only when it carries a fetchable
 * `application/xml` attachment and nothing else `DocumentAttachment` can already render inline —
 * so we never override an existing PDF/HTML rendition with the transform.
 */
export const needsCcdaRender = (doc: DocumentReference): boolean =>
  !hasInlineRenderableContent(doc) && Boolean(findCcdaContent(doc));

/**
 * Prepend a synthetic `text/html` content entry carrying the rendered CDA (base64, matching how
 * `DocumentAttachment` decodes inline `data`). `DocumentReference.pickPreferredContent` then prefers
 * it over the raw XML — which alone would fall through to the download link — with no changes needed
 * in `@liza/ui`. The original content is kept so nothing is lost if the html entry is ever ignored.
 */
export const withRenderedCcda = (doc: DocumentReference, html: string): DocumentReference => ({
  ...doc,
  content: [
    {
      attachment: {
        contentType: 'text/html',
        data: encodeBase64Utf8(html),
        title: findCcdaContent(doc)?.attachment.title,
      },
    },
    ...(doc.content ?? []),
  ],
});
